/** Speaker names, matching the track names the simulator publishes. */
export const AGENT = "agent"
export const PERSONA = "persona"

export type SimulationRoomStatus =
  | "idle"
  | "connecting"
  | "waiting"
  | "connected"
  | "stalled"
  | "live"
  | "complete"
  | "error"

export interface SimulationRoomSegment {
  segmentId: string
  seq: number
  /**
   * The lowest sequence number seen for this segment id. A segment can be
   * re-emitted with a corrected end offset (and a higher seq) after other
   * speakers' segments have already arrived, so transcript order goes by
   * where the segment first appeared, not by its latest revision.
   */
  orderSeq: number
  speaker: string
  text: string
  isFinal: boolean
  /**
   * Seconds into the call, so a turn can carry an interval. The protocol
   * permits either to be absent; absence must survive to the UI rather than
   * turn into a fabricated 0:00.
   */
  startOffsetSeconds?: number
  endOffsetSeconds?: number
}

/**
 * A non-speech transcript entry: a tool call or a websocket event. Mirrors the
 * stored transcript's message shape (role, name, JSON content, owner), so the
 * live view renders the same thing the stored transcript will show later.
 * Unlike a segment, an event is never revised — one emission is the whole story.
 */
export interface SimulationRoomEvent {
  eventId: string
  seq: number
  /** Same first-emission ordering rule as segments (replay can re-deliver). */
  orderSeq: number
  /** "tool" or "websocket_event", as the stored transcript spells them. */
  role: string
  /** JSON containing only the event fields rendered in the transcript. */
  content: string
  name: string
  /** "agent" or "persona"; absent for websocket events. */
  toolCallOwner?: string
  startOffsetSeconds?: number
  endOffsetSeconds?: number
}

/** A data message published into the room by the simulation worker. */
export interface Envelope {
  v: number
  seq: number
  type: string
  payload: Record<string, unknown>
}

export interface SimulationRoomOptions {
  /** Whether the simulation is still running (drives the join timeout). */
  isLive?: boolean
  /**
   * Open the session with the gain already at zero, for mounts that join for
   * the transcript (data channel) rather than for audio. Read once on mount;
   * a later toggleMute (a user gesture) unmutes as usual.
   */
  startMuted?: boolean
}

export interface SimulationRoom {
  status: SimulationRoomStatus
  error: string | null
  /** Seconds since the simulation started, corrected for device clock skew. */
  elapsedSeconds: number | null
  /** Current level per speaker, 0..1, for the activity indicator. */
  levels: Record<string, number>
  segments: SimulationRoomSegment[]
  events: SimulationRoomEvent[]
  isMuted: boolean
  toggleMute: () => void
  start: () => void
  stop: () => void
}
